[
  {
    "slug": "embed-a-quiz-on-your-website",
    "path": "/guides/embed-a-quiz-on-your-website",
    "title": "Put a Quiz on Your Website",
    "description": "Install and verify a Quiz on your website before sending readers to that page.",
    "requirements": [
      "A Quiz with ready questions",
      "A Business Plan or Enterprise Plan",
      "Access to add embed code to a public page, or someone who can add it for you"
    ],
    "steps": [
      {
        "title": "Choose the page",
        "body": "Create or open a dedicated public page. Place the Quiz directly below your site header, with ads, signup forms, sharing tools, and other content below it. Do not load content above the Quiz after the page opens. Copy its full URL.",
        "doneWhen": "You have the page's HTTPS URL."
      },
      {
        "title": "Approve the website domain",
        "body": "In Atrivial, open Account. Under Embed and Approved domains, add the page's domain without the path.",
        "doneWhen": "The domain appears under Approved domains."
      },
      {
        "title": "Copy the website embed",
        "body": "Open the Quiz and select Output. Below View link, expand View embed code, then use its copy button.",
        "doneWhen": "The copy button reads Copied!"
      },
      {
        "title": "Add and verify the embed",
        "body": "Keep Atrivial-hosted selected while you add the copied code to the page's HTML, either directly or through your publishing platform's code or embed feature. Do not override the iframe height in site CSS; use the data-height value in the copied code. If someone else manages the website, send them the code and page URL.",
        "doneWhen": "The published page supports one full Quiz playthrough on both desktop and mobile."
      },
      {
        "title": "Send players to your website",
        "body": "Return to the Quiz in Atrivial. Select Style & Sponsors. Under Where will players play this quiz?, choose My website.",
        "doneWhen": "The page URL field appears."
      },
      {
        "title": "Save the page URL",
        "body": "Paste the full page URL into Quiz page URL, then leave the field to save it.",
        "doneWhen": "The Quiz header shows Saved."
      }
    ],
    "troubleshooting": {
      "title": "If the embed does not appear",
      "body": "Use the copied code unchanged. If article cards or sign-in do nothing when tapped, replace the published embed with a fresh copy from Output. If your website rejects, changes, or removes it, Atrivial Support can provide an iframe version.",
      "actionLabel": "Contact Atrivial Support"
    }
  },
  {
    "slug": "add-atrivial-to-kit",
    "path": "/guides/add-atrivial-to-kit",
    "title": "Add Atrivial to Kit",
    "description": "Install the Atrivial Quiz block in Kit so trivia appears in your broadcasts.",
    "requirements": [
      "An Atrivial subscription on Individual or higher",
      "A Kit account",
      "Access to edit a Kit broadcast or template"
    ],
    "steps": [
      {
        "title": "Install Atrivial from Kit",
        "body": "In Kit, open Apps and install Atrivial. Kit opens Atrivial in your browser.",
        "doneWhen": "You see Connect Atrivial to Kit."
      },
      {
        "title": "Sign in or create an Atrivial account",
        "body": "Sign in, or create an account and start the 14-day trial. Use the Atrivial account that should own the quizzes.",
        "doneWhen": "You see the consent screen."
      },
      {
        "title": "Allow Atrivial in Kit",
        "body": "Choose the Atrivial account if you have more than one, then select Allow. Kit then asks you to let Atrivial read your published posts so the recent-posts source can work.",
        "doneWhen": "Kit finishes installing the app."
      },
      {
        "title": "Add the Atrivial Quiz block",
        "body": "In a Kit broadcast or template, add the Atrivial Quiz block. Under Emails, pick Recent for quizzes from your latest emails (first edition covers about a week, then editions use emails since the last quiz), or Specific to hand-pick one or more sent broadcasts — Specific quizzes stay fixed. You can also paste a link/text or choose a Library quiz. Change styling and answer interactions in Atrivial via Style Preset.",
        "doneWhen": "The block shows a question, a button, or a Generating placeholder."
      },
      {
        "title": "Send a test",
        "body": "Send the broadcast to yourself and tap an answer. You should land on the hosted quiz with that answer selected.",
        "doneWhen": "The hosted quiz opens from the email."
      }
    ],
    "troubleshooting": {
      "title": "If latest-post asks you to reconnect",
      "body": "Open Atrivial from Kit again so posts access can finish. Per-broadcast click stats are not available; Kit clicks still appear in Atrivial as Kit.",
      "actionLabel": "Contact Atrivial Support"
    }
  },
  {
    "slug": "add-auto-updating-quiz-to-email",
    "path": "/guides/add-auto-updating-quiz-to-email",
    "title": "Add an auto-updating quiz to your email",
    "description": "Install the code once in a reusable email template. The featured question updates when a new quiz goes live.",
    "requirements": [
      "A recurring quiz series with published questions",
      "Access to an email editor that accepts custom HTML"
    ],
    "steps": [
      {
        "title": "Copy the email code",
        "body": "Open your quiz series in Atrivial. In Email, check that your email provider is selected. Open Output and choose Copy code under Put this in your email.",
        "doneWhen": "The copy button reads Copied!"
      },
      {
        "title": "Add an HTML block to your email template",
        "body": "Open a reusable newsletter template in your email provider. At the place where readers should see the question, add a Custom HTML or Code block. Paste the copied code into that block, rather than a text block. The block name varies by provider.",
        "doneWhen": "Your email preview shows the question and answer choices instead of HTML code."
      },
      {
        "title": "Save the reusable template",
        "body": "Save the template and use it to create future newsletters. Keep the copied code unchanged, including its image URLs and any template tags added for your provider.",
        "doneWhen": "A newsletter created from the saved template includes the quiz question."
      },
      {
        "title": "Send yourself a test",
        "body": "Send a test from your email provider and open it on desktop and a phone. Click an answer to check that it opens the intended quiz page with that answer selected, then continue through the quiz.",
        "doneWhen": "The delivered email displays the question and its answer links work."
      },
      {
        "title": "Reuse the code for the next send",
        "body": "When the next quiz in the series goes live, create your newsletter from the same template. You do not need to copy new code for each quiz. Check the live questions and send yourself another test before sending to subscribers.",
        "doneWhen": "Your next newsletter uses the saved code and shows the newly published question."
      }
    ],
    "troubleshooting": {
      "title": "If the question is missing or out of date",
      "body": "If you see HTML code, move it into a Custom HTML or Code block. If the question is old, confirm that the next quiz is live. Some email apps cache images and may still show the previous question. If your editor cannot accept custom HTML, choose URL link in Atrivial and add it as a normal email link or button. Substack requires this link format.",
      "actionLabel": "Contact Atrivial Support"
    }
  },
  {
    "slug": "add-quiz-to-email",
    "path": "/guides/add-quiz-to-email",
    "title": "Add a fixed quiz to your email",
    "description": "Place a quiz question in one newsletter using the copied HTML. Copy fresh code whenever you change its questions.",
    "requirements": [
      "A quiz with ready questions",
      "Access to an email editor that accepts custom HTML"
    ],
    "steps": [
      {
        "title": "Copy the email code",
        "body": "Open your quiz series in Atrivial and review the questions you want to send. In Email, check that your email provider is selected. Open Output and choose Copy code under Put this in your email.",
        "doneWhen": "The copy button reads Copied!"
      },
      {
        "title": "Add an HTML block to your newsletter",
        "body": "Open the newsletter in your email provider. At the place where readers should see the question, add a Custom HTML or Code block. Paste the copied code into that block, rather than a text block. The block name varies by provider.",
        "doneWhen": "Your email preview shows the question and answer choices instead of HTML code."
      },
      {
        "title": "Send yourself a test",
        "body": "Send a test from your email provider and open it on desktop and a phone. Click an answer to check that it opens the intended quiz page with that answer selected, then continue through the quiz.",
        "doneWhen": "The delivered email displays the intended question and its answer links work."
      },
      {
        "title": "Replace the code when questions change",
        "body": "This email HTML is a snapshot. After changing the questions in Atrivial, return to Output, copy fresh code, and replace the entire HTML block in your unsent newsletter. Send another test before sending to subscribers.",
        "doneWhen": "Your newsletter preview and test email show the updated question."
      }
    ],
    "troubleshooting": {
      "title": "If the email shows code or the wrong question",
      "body": "If you see HTML code, move it into a Custom HTML or Code block. If the question is old, replace the block with a fresh copy from Output. If your editor cannot accept custom HTML, choose URL link in Atrivial and add it as a normal email link or button. Substack requires this link format.",
      "actionLabel": "Contact Atrivial Support"
    }
  }
]
